'use client'

import { UserApi } from '@/api/user'
import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { IImage } from '@/api/interfaces/IImage'
import { WithT } from '@/locales/types'
import { createT } from '@/locales/index'
import { GenericLink } from '../GenericLink'
import classNames from 'classnames'
import { Icon } from '../Icon'

type Props = {
  user: {
    username: string
    nickname: string
    profileImage: IImage | null
    isFollowing?: boolean
    followerCount?: number
  }
  isFollow?: boolean
  hideCount?: boolean
  onChange?: () => void
}

export const FollowButton = ({
  user,
  isFollow,
  hideCount,
  translations,
  onChange,
}: WithT<Props>) => {
  const t = createT(translations)
  const router = useRouter()
  const onClick = async () => {
    await UserApi().follow(user.username)
    router.refresh()
    onChange?.()
  }

  const label = useMemo(() => {
    const text = isFollow ? t('following') : t('follow')

    if (hideCount) {
      return text
    }

    return `${text} ${user.followerCount || 0}`
  }, [user, hideCount, t, isFollow])

  return (
    <GenericLink
      onClick={onClick}
      className="flex rounded-full gap-1 px-2.5 py-2 bg-gray-100 items-center shrink-0 max-h-[30px]"
      privilege
    >
      <Icon
        name={isFollow ? 'heart-fill' : 'heart-line'}
        size={14}
        className={classNames({
          '!text-key': isFollow,
        })}
      />
      <span
        className={classNames(
          'text-body3 font-medium text-gray-900 whitespace-nowrap',
          {
            '!text-key': isFollow,
          },
        )}
      >
        {label}
      </span>
    </GenericLink>
  )
}
